/**
 * Compléter un achat en gemmes — **logique pure** (ROADMAP 1.3).
 *
 * ══ Le défaut que ce module referme ══════════════════════════════════════════
 *
 * Une carte de boutique trop chère est désactivée et **muette**. Le joueur tape dessus,
 * rien ne se passe, et rien ne lui dit quoi faire. C'est la demande de Benoit du 19 août,
 * mot pour mot : « plus de bouton non cliquable ».
 *
 * ══ POURQUOI CE MODULE N'INVENTE AUCUN PRIX ══════════════════════════════════
 *
 * `GEMMES.md` G§0 interdit d'ajouter un prix sans en discuter. On n'en ajoute pas : le
 * barème existe déjà, et il est publié.
 *
 * `GEM_SPENDING.skip` vend des **secondes de production** — 15 gemmes pour une heure,
 * 40 pour quatre, 90 pour douze. Or compléter un achat, c'est exactement acheter le
 * temps qu'il aurait fallu attendre pour gagner les pièces manquantes. Le joueur PEUT
 * déjà le faire en deux gestes : acheter une avance, puis acheter son bâtiment. Ce
 * module ne crée donc aucune capacité nouvelle — **il retire deux taps à une action déjà
 * permise**, au prix déjà publié.
 *
 * Conséquence directe, et c'est elle qui garde le §14 intact : le prix DOIT être celui
 * de l'avance équivalente. Moins cher, on ouvrirait un raccourci que l'offre existante
 * ne donne pas ; plus cher, on ferait payer deux fois la même chose.
 *
 * ══ Ce qui en découle, et qui n'est pas négociable non plus ══════════════════
 *
 * Puisque c'est une avance, elle **consomme un des trois créneaux quotidiens**. Sans
 * cela, le plafond de `skip` — « sans ce plafond, l'avance devient la production
 * principale » — se contournerait en passant par la boutique au lieu du panneau gemmes.
 * Un plafond qu'on peut éviter par un autre bouton n'est pas un plafond.
 *
 * Et comme l'avance, le complément **ne crédite pas `totalProduced`** : ni jalon, ni
 * étoile de prestige, ni rang de ligue. Les gemmes achètent des pièces, jamais de la
 * progression.
 */

import { GEM_SPENDING } from '@/config/balance';

import { currentProduction, skipsUsedToday } from './gameState';
import type { CitySnapshot } from './gameState';
import { dayNumber } from './quests';

/**
 * Prix en gemmes de N secondes de production, dérivé de l'échelle `skip`.
 *
 * Interpolation linéaire par morceaux entre les paliers publiés, prolongée au-delà du
 * dernier à son propre taux. La courbe est donc **exactement** celle que le joueur voit
 * dans le panneau gemmes : aucun nombre nouveau, et une retouche de l'échelle se propage
 * ici sans qu'on y pense (règle 15).
 *
 * Rend `null` si la durée n'est pas un nombre fini positif — l'appelant ne propose alors
 * rien plutôt que d'afficher un prix absurde.
 */
export function gemsForSeconds(seconds: number): number | null {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;

  // `tiers` est un tuple `as const` : le compilateur connaît ses bornes, donc un garde
  // sur `undefined` ici serait du code mort — ESLint le refuse, à raison.
  const tiers = GEM_SPENDING.skip.tiers;
  const first = tiers[0];
  const last = tiers[tiers.length - 1] ?? first;

  // Sous le premier palier : son taux, au prorata. Un joueur à qui il manque cinq
  // minutes ne doit pas payer une heure — ce serait un refus déguisé.
  if (seconds <= first.seconds) {
    return Math.max(1, Math.ceil((seconds / first.seconds) * first.cost));
  }

  for (let i = 1; i < tiers.length; i++) {
    const lo = tiers[i - 1];
    const hi = tiers[i];
    if (lo === undefined || hi === undefined) continue;
    if (seconds <= hi.seconds) {
      const t = (seconds - lo.seconds) / (hi.seconds - lo.seconds);
      return Math.ceil(lo.cost + (hi.cost - lo.cost) * t);
    }
  }

  // Au-delà du dernier palier, on prolonge à SON taux — le plus avantageux de l'échelle.
  // Le prolonger au taux du premier ferait exploser le prix des gros compléments et
  // ramènerait le bouton muet sous une autre forme.
  return Math.ceil((seconds / last.seconds) * last.cost);
}

/**
 * Prix en gemmes pour combler un manque de pièces, au taux de production courant.
 *
 * Rend `null` quand la ville ne produit rien : sans production, « le temps qu'il aurait
 * fallu attendre » est infini, et il n'existe aucun prix juste. L'appelant propose alors
 * d'attendre ou de poser un premier bâtiment — jamais un chiffre inventé.
 */
export function gemsForCoins(missing: number, perSecond: number): number | null {
  if (!Number.isFinite(missing) || missing <= 0) return 0;
  if (!Number.isFinite(perSecond) || perSecond <= 0) return null;
  return gemsForSeconds(missing / perSecond);
}

export type TopUpRefusal =
  'nothing-missing' | 'no-production' | 'too-few-gems' | 'daily-cap' | 'invalid';

export interface TopUpQuote {
  /** Pièces qu'il manque pour atteindre la cible. */
  readonly missing: number;
  /** Prix en gemmes, `null` si aucun prix juste n'existe. */
  readonly gems: number | null;
  /** Compléments encore disponibles aujourd'hui — partagés avec l'avance de production. */
  readonly remainingToday: number;
  /** Pourquoi l'offre n'est pas proposable, `null` si elle l'est. */
  readonly refusal: TopUpRefusal | null;
}

/**
 * Ce que coûterait le complément, et s'il est possible — **sans rien modifier**.
 *
 * Séparé de l'achat pour que l'interface puisse afficher le prix sur une carte encore
 * inactive. C'est tout l'objet du lot : une carte trop chère montre ce qu'elle coûterait
 * en gemmes AVANT qu'on la touche, au lieu de rester grise et silencieuse.
 */
export function quoteTopUp(snapshot: CitySnapshot, target: number, now: number): TopUpQuote {
  const remainingToday = Math.max(0, GEM_SPENDING.skip.dailyCap - skipsUsedToday(snapshot, now));

  if (!Number.isFinite(target) || target <= 0) {
    return { missing: 0, gems: null, remainingToday, refusal: 'invalid' };
  }

  const missing = target - snapshot.coins;
  if (missing <= 0) {
    return { missing: 0, gems: null, remainingToday, refusal: 'nothing-missing' };
  }

  const gems = gemsForCoins(missing, currentProduction(snapshot));
  if (gems === null) {
    return { missing, gems: null, remainingToday, refusal: 'no-production' };
  }

  if (remainingToday <= 0) {
    return { missing, gems, remainingToday, refusal: 'daily-cap' };
  }
  if (snapshot.gems < gems) {
    return { missing, gems, remainingToday, refusal: 'too-few-gems' };
  }

  return { missing, gems, remainingToday, refusal: null };
}

export interface TopUpResult {
  readonly snapshot: CitySnapshot;
  readonly ok: boolean;
  readonly refusal?: TopUpRefusal;
  /** Pièces réellement créditées. */
  readonly coins: number;
  /** Gemmes réellement dépensées. */
  readonly gems: number;
}

/**
 * Complète le solde de pièces jusqu'à `target`, contre des gemmes.
 *
 * ⚠️ Crédite **exactement** ce qui manque, jamais un montant rond. Arrondir vers le haut
 * laisserait un reliquat de pièces après l'achat, et `GEMMES.md` interdit explicitement
 * les offres calibrées pour laisser un reste — c'est la pratique qui fait la plus
 * mauvaise réputation aux jeux gratuits, et elle se glisserait ici sans qu'on la voie.
 *
 * ⚠️ N'achète PAS l'objet visé. Le joueur reste maître du geste suivant : il peut changer
 * d'avis et dépenser ses pièces ailleurs. Enchaîner les deux ferait de ce bouton un achat
 * en un tap dont le prix réel serait invisible.
 */
export function topUpCoins(snapshot: CitySnapshot, target: number, now: number): TopUpResult {
  const quote = quoteTopUp(snapshot, target, now);
  if (quote.refusal !== null || quote.gems === null) {
    return { snapshot, ok: false, refusal: quote.refusal ?? 'invalid', coins: 0, gems: 0 };
  }

  return {
    ok: true,
    coins: quote.missing,
    gems: quote.gems,
    snapshot: {
      ...snapshot,
      coins: snapshot.coins + quote.missing,
      gems: snapshot.gems - quote.gems,
      // Le MÊME compteur que l'avance de production : c'est le même acte économique, et
      // deux compteurs séparés feraient six avances par jour au lieu de trois.
      skipDay: dayNumber(now),
      skipUsed: GEM_SPENDING.skip.dailyCap - quote.remainingToday + 1,
      updatedAt: now,
    },
  };
}
